import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import { ReceivingResponse } from "../../app/models/responseModels/receivingResponse";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import {
  downloadReceivingDataToCsv,
  downloadReceivingDataToXlsx,
  getReceivingList,
  processBarcodeForReceiving,
} from "../../app/features/receiving/receivingSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { useSelector } from "react-redux";
import { PACKAGE_STATUS_ARRAY } from "../helper/helper";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import { ReceivingDownloadRequest } from "../../app/models/requestModels/receivingDownloadRequest";
import { DOWNLOAD_FILE_TYPE } from "../../enums/DownloadFileEnum";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import ReceivingDataFilter from "./ReceivingDataFilter";
import useScanDetection from "./useScanDetection";
import {
  RECEIVING_COLUMN_NAME,
  RECEIVING_LABEL_NAME,
  isDeliveryToday,
} from "./ReceivingConstants";
import { BarcodeReceivingModel } from "../../app/models/requestModels/processBarcode";
import { ParcelCenterResponse } from "../../app/models/responseModels/parcelCenterResponse";
import { PrintBarcode, getZebraPrinter } from "./PrintBarcode";
import { useSubmitModalWithParameter } from "../../components/common/modalPopup/useSubmitModal";
import ViewReceivingModal from "./modals/ViewReceivingModal";
import {
  FDC_Message,
  beepFail,
  beepPass,
} from "../../functions/fdcMessage/FdcMessage";
//import BarcodeReader from "react-barcode-reader"; BarcodeReader code

const breadcrumb = ["Receiving"];

export function ReceivingDownloadDataModel(
  receivingData: ReceivingResponse[],
  fileType: DOWNLOAD_FILE_TYPE
): ReceivingDownloadRequest {
  return {
    receivingData: receivingData,
    downloadFileType: fileType,
  };
}

const Receiving = () => {
  const dispatch = useAppDispatch();
  const { isLoading, receivingList } = useSelector(
    (state: RootState) => state.receiving
  );

  // ***** get parcel center assigned to user *****
  const assignedParcelCenters = useSelector(
    (state: RootState) =>
      state.login?.authResponse?.userSettings?.assignedParcelCenters
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [barcode, setBarcode] = useState<String | null>(null);
  const [scanParcelCenter, setscanParcelCenter] = useState("");
  const [scanParcelCenterOptions, setscanParcelCenterOptions] = useState<
    ParcelCenterResponse[]
  >([]);

  const [filteredData, setFilteredData] = useState<ReceivingResponse[]>([]);

  // ***** Callbacks *****
  const getReceivingData = useCallback(async () => {
    try {
      const response = await dispatch(getReceivingList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "receivingList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "receivingListException",
      });
    }
  }, [dispatch]);

  const processScanRequest = useCallback(
    async (barcode: String) => {
      try {
        const barcodeDetails = BarcodeReceivingModel(barcode, scanParcelCenter);
        setBarcode(null);
        if (barcodeDetails.barcode.toLocaleLowerCase().includes("control")) {
          return;
        }
        if (barcodeDetails.barcode.length < 6) {
          FDC_Message("red", "Invalid barcode", 3000);
          beepFail();
          return;
        }
        if (barcodeDetails.parcelCenterId === "") {
          toast.error("Please select parcel center before scanning");
          return;
        }
        const response = await dispatch(
          processBarcodeForReceiving(barcodeDetails)
        ).unwrap();
        if (!response.isSuccess) {
          FDC_Message("red", response.message, 3000);
          beepFail();
        } else {
          const receivedPackage = response.data;
          if (isDeliveryToday(receivedPackage?.packageDeliveryDate!)) {
            const message = `TrackingNumber: ${receivedPackage?.trackingNumber} Route: ${receivedPackage?.packageWorkArea} SID: ${receivedPackage?.packageSid}`;
            FDC_Message("springgreen", message, 3000);
            beepPass();
            try {
              const printerStatus = await getZebraPrinter();
              if (printerStatus.errors) {
                console.log(printerStatus.errors);
              } else {
                console.log(printerStatus.isReadyToPrint);
              }
              if (
                !printerStatus.isReadyToPrint ||
                !receivedPackage?.packageWorkArea ||
                !receivedPackage?.packageSid
              ) {
                //Alert user No Route Information
                if (!receivedPackage?.packageWorkArea) {
                  FDC_Message("red", "No route information", 3000);
                  beepFail();
                }
                console.log("Route Information or Device is not found");
              } else {
                //Print Sort Label if printer available
                PrintBarcode(
                  receivedPackage!.packageWorkArea,
                  receivedPackage!.packageSid
                );
              }
            } catch {
              console.log("Error connecting to zebra printer");
            }
          } else {
            FDC_Message(
              "red",
              "Not Scheduled for Today. Please send to RTS Station",
              3000
            );
            beepFail();
          }
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: `processScanRequestException${barcode}`,
        });
      }
    },
    [dispatch, scanParcelCenter]
  );

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getReceivingData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getReceivingData]);

  useEffect(() => {
    if (receivingList.length > 0) {
      if (filteredData.length === receivingList.length) {
        setFilteredData(receivingList);
      }
    } else {
      setFilteredData([]);
    }
  }, [receivingList, filteredData]);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan && barcode) {
      processScanRequest(barcode);
    }
    return () => {
      isFunctionRan = false;
    };
  }, [barcode, processScanRequest]);

  useEffect(() => {
    if (assignedParcelCenters) {
      setscanParcelCenterOptions(assignedParcelCenters);
      if (assignedParcelCenters.length === 1) {
        setscanParcelCenter(assignedParcelCenters[0].parcelCenterId);
      }
    }
  }, [dispatch, assignedParcelCenters]);

  // ***** Scan Detection *****
  useScanDetection({
    onComplete: (code) => {
      setBarcode(code);
    },
    minLength: 3,
  });

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<ReceivingResponse>[]>(
    () => [
      {
        accessorKey: RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME,
        header: RECEIVING_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME,
        size: 150,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.CLIENT_NAME,
        header: RECEIVING_LABEL_NAME.CLIENT_NAME,
        size: 180,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME,
        header: RECEIVING_LABEL_NAME.FDP_COMPANY_NAME,
        size: 200,
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_FDP_ID,
        header: RECEIVING_LABEL_NAME.PACKAGE_FDP_ID,
        size: 80,
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_TRACKING_NUMBER,
        header: RECEIVING_LABEL_NAME.PACKAGE_TRACKING_NUMBER,
        size: 230,
      },
      {
        filterVariant: "date",
        accessorKey: RECEIVING_COLUMN_NAME.ARRIVAL_DATE,
        accessorFn: (row) => new Date(row.arrivalDate),
        header: RECEIVING_LABEL_NAME.ARRIVAL_DATE,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h24",
              })}
            </span>
          </div>
        ),
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE,
        filterVariant: "date",
        accessorFn: (row) => new Date(row.packageDeliveryDate),
        header: RECEIVING_LABEL_NAME.PACKAGE_DELIVERY_DATE,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            <span>
              {new Date(cell.getValue<string>()).toLocaleDateString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA,
        header: RECEIVING_LABEL_NAME.PACKAGE_WORK_AREA,
        size: 100,
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_SID,
        header: RECEIVING_LABEL_NAME.PACKAGE_SID,
        size: 80,
      },
      {
        accessorKey: RECEIVING_COLUMN_NAME.PACKAGE_STATUS,
        header: RECEIVING_LABEL_NAME.PACKAGE_STATUS,
        size: 200,
        filterVariant: "multi-select",
        filterSelectOptions: PACKAGE_STATUS_ARRAY,
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getReceivingData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadReceivingDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "receivingDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "receivingDownloadCsvException",
      });
    }
    handleClose();
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadReceivingDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "Receiving.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "receivingDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "receivingDownloadXlsxException",
      });
    }
  };

  const handleScanParcelCenterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let selectedOption = event.target.value as string;
    setscanParcelCenter(selectedOption);
  };

  // BarcodeReader code
  // const handleScan = (data: string) => {
  //   setBarcode(data);
  // };

  // const handleScanError = (err: Error) => {
  //   console.error(err);
  // };

  // ***** Modal Popups *****
  const [viewReceivingModal, openViewReceivingModal] =
    useSubmitModalWithParameter<ReceivingResponse>(
      (receivingRowData, closeViewReceivingModal) => (
        <ViewReceivingModal
          closeViewReceivingModal={closeViewReceivingModal}
          onCompleted={getReceivingData}
          receivingRowData={receivingRowData}
        />
      )
    );

  // ***** Datagrid Custom Actions *****

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<ReceivingResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        <TextField
          select
          key="userParcelCenter"
          margin="none"
          id="userParcelCenter"
          label="Parcel Center"
          name="userParcelCenter"
          size="small"
          value={scanParcelCenter}
          onChange={handleScanParcelCenterChange}
          sx={{ minWidth: 125, ml: 1 }}
        >
          {scanParcelCenterOptions.map((Option, index) => (
            <MenuItem value={Option.parcelCenterId} key={Option.parcelCenterId}>
              {Option.parcelCenterName}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <ReceivingDataFilter
          receivingData={receivingList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      {/* <BarcodeReader onError={handleScanError} onScan={handleScan} /> BarcodeReader code */}
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No receiving packages"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="arrivalDate"
        openModal={openViewReceivingModal}
      />
      {viewReceivingModal}
    </Box>
  );
};

export default Receiving;
